import type { DictionaryT, OptionRowT } from "@patient-watch/types";

/** A dictionary row with its options embedded, as PostgREST returns `dictionary:dictionaries(*, options(*))`. */
export type DictionaryWithEmbeddedOptions = DictionaryT & {
    options?: OptionRowT[] | null;
};

function bySortOrder(a: OptionRowT, b: OptionRowT): number {
    return a.sort_order - b.sort_order || a.created_at.localeCompare(b.created_at);
}

/**
 * Turns an `inputs` row fetched with `dictionary:dictionaries(*, options(*))` into the shape pages_view
 * returns: `dictionary` without nested options, plus `option_rows` in sort order.
 * Inputs with no dictionary come back as they were, so there is nothing to opt into.
 */
export function attachDictionaryRows<T extends { dictionary?: unknown }>(
    row: T
): T & { option_rows?: OptionRowT[] | null } {
    const embedded = row.dictionary as DictionaryWithEmbeddedOptions | null | undefined;
    if (!embedded) return row;

    const { options, ...dictionary } = embedded;
    return {
        ...row,
        dictionary,
        option_rows: [...(options ?? [])].sort(bySortOrder),
    };
}

/**
 * `dictionary` and `option_rows` are read-only extras that pages_view and the by-id loader add.
 * They are not columns on `inputs`, so drop them before any insert or upsert.
 */
export function toInputRow<T extends object>(input: T): T {
    const {
        dictionary: _dictionary,
        option_rows: _optionRows,
        ...row
    } = input as T & {
        dictionary?: unknown;
        option_rows?: unknown;
    };
    return row as T;
}
